import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  menuApi,
  menuKeys,
  useMenuScope,
  type Menu,
  type MenuProduct,
  type ProductInput,
} from '@/entities/menu';

/**
 * A create (no id) or an update (existing id) of a product. A discriminated
 * union so the id is present exactly when the update branch needs it.
 */
export type UpsertProductVars =
  | ({ id: MenuProduct['id'] } & ProductInput)
  | ({ id?: undefined } & ProductInput);

/**
 * The submitted fields as a patch over the cached card.
 *
 * Built field by field rather than by spreading `vars`, because the two shapes
 * genuinely differ: `id` is an addressing hint, not a product field, and the
 * catalog's `name`/`description` are locale-resolved server-side, so they are
 * patched
 * from the canonical `ru` translation instead of going stale while the refetch
 * is in flight.
 */
function toCachePatch(vars: UpsertProductVars): Partial<MenuProduct> {
  return {
    categoryId: vars.categoryId,
    // A create with no price patches none — the card falls back to the
    // server's answer on refetch rather than showing a bogus 0.
    ...(vars.price === undefined ? {} : { price: vars.price }),
    name: vars.nameTranslations.ru,
    nameTranslations: vars.nameTranslations,
    description: vars.descriptionTranslations.ru,
    descriptionTranslations: vars.descriptionTranslations,
    alwaysReady: vars.alwaysReady,
    ...(vars.modifiersDisabled === undefined
      ? {}
      : { modifiersDisabled: vars.modifiersDisabled }),
    ...(vars.branchIds === undefined ? {} : { branchIds: vars.branchIds }),
    // Photos are deliberately NOT patched. What the submit carries is storage
    // KEYS, while the card renders `imageUrl` — an absolute URL the backend
    // expands from a key. Patching a key into `imageUrl` would point the card's
    // `<img src>` at a relative path and break the thumbnail until the refetch
    // landed, so the old URL stays until the server answers with the new one.
    ...(vars.cookTimeSeconds === undefined
      ? {}
      : { cookTimeSeconds: vars.cookTimeSeconds }),
  };
}

/**
 * Create or update a product.
 *
 * An UPDATE patches the cache optimistically — the submitted fields are the
 * whole change. A CREATE does not: the id is server-minted, and the server also
 * derives `effective_price` from any active promotion, so a locally-built card
 * could show the wrong price until the refetch corrected it. Both invalidate
 * the catalog on settle.
 */
export function useUpsertProduct() {
  const scope = useMenuScope();
  const queryClient = useQueryClient();
  // The PREFIX, not one entry: the catalog is cached per scope («Все филиалы»
  // and each branch) and per filter set, and an edit shows in all of them.
  const catalogKey = menuKeys.catalogs();

  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id, ...input }: UpsertProductVars) =>
      id === undefined
        ? menuApi.createProduct(scope, input)
        : menuApi.updateProduct(scope, id, input),

    onMutate: async (
      vars,
    ): Promise<{ previous: [readonly unknown[], Menu | undefined][] }> => {
      if (vars.id === undefined) return { previous: [] };
      await queryClient.cancelQueries({ queryKey: catalogKey });
      // Snapshot and patch EVERY cached catalog entry. Which one is on screen
      // depends on the active scope and filters, so patching a single key left
      // the edit invisible on any other — and unrolled-back on failure.
      const previous = queryClient.getQueriesData<Menu>({ queryKey: catalogKey });
      // The catalog's display `name` is locale-resolved server-side, so it is
      // patched from the canonical `ru` translation rather than left stale.
      const patch = toCachePatch(vars);
      for (const [key, menu] of previous) {
        // The prefix also covers the taxonomy list (`MenuCategory[]`), which
        // has no `.products` — reading it threw, rejected `onMutate`, and the
        // request never fired. Only full catalogs get patched.
        if (!menu || !Array.isArray(menu.products)) continue;
        queryClient.setQueryData<Menu>(key, {
          ...menu,
          products: menu.products.map((product) =>
            product.id === vars.id ? { ...product, ...patch } : product,
          ),
        });
      }
      return { previous };
    },

    onError: (_error, _vars, context) => {
      for (const [key, menu] of context?.previous ?? []) {
        queryClient.setQueryData(key, menu);
      }
    },

    onSettled: async (_data, _error, vars) => {
      await queryClient.invalidateQueries({ queryKey: catalogKey });
      // Also drop the per-product record the editor seeds from. Without this a
      // reopened drawer replays the PRE-EDIT values: the record is `staleTime:
      // 0`, but React Query still serves the cached copy synchronously while it
      // refetches, and the drawer seeds once on open — so the stale copy is
      // what lands in the form, and submitting it writes the old name/price
      // straight back over the edit that just succeeded.
      if (vars.id !== undefined) {
        await queryClient.invalidateQueries({
          queryKey: menuKeys.product(scope.restaurantId, vars.id),
        });
      }
    },
  });
}
